export type InlineToken =
  | { type: "text"; value: string }
  | { type: "bold"; value: string }
  | { type: "italic"; value: string }
  | { type: "code"; value: string }
  | { type: "link"; value: string; href: string }
  | { type: "image"; alt: string; src: string };

/**
 * Only schemes that are safe to hand to `href`/`src` on content we didn't author.
 * `javascript:`/`data:` are the ones that matter — a support agent's message is untrusted
 * input as far as the app is concerned.
 */
const isSafeUrl = (value: string, allowMailto: boolean): boolean => {
  try {
    const { protocol } = new URL(value, window.location.origin);
    return protocol === "http:" || protocol === "https:" || (allowMailto && protocol === "mailto:");
  } catch {
    return false;
  }
};

export const isSafeLinkUrl = (value: string) => isSafeUrl(value, true);
export const isSafeImageUrl = (value: string) => isSafeUrl(value, false);

/**
 * Deliberately narrow: images, links, `**bold**`, `*italic*` and `` `code` ``. Support
 * replies arrive as TipTap JSON (`rich_content`), which is what we render when it's there —
 * this only covers the plain-text `content` fallback, where markdown syntax would otherwise
 * show up raw. No `_underscore_` emphasis, since it would mangle snake_case identifiers in
 * the kind of message where someone pastes an error string.
 */
const INLINE_PATTERN =
  /!\[([^\]]*)\]\(([^\s)]+)\)|\[([^\]]+)\]\(([^\s)]+)\)|\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`\n]+)`/g;

export const parseInlineMarkdown = (input: string): InlineToken[] => {
  const tokens: InlineToken[] = [];
  let lastIndex = 0;

  const pushText = (value: string) => {
    if (!value) return;

    const previous = tokens[tokens.length - 1];
    if (previous?.type === "text") {
      previous.value += value;
      return;
    }

    tokens.push({ type: "text", value });
  };

  // `matchAll` needs the global flag, and reusing a module-level regex with `exec` would
  // carry lastIndex between calls.
  for (const match of input.matchAll(INLINE_PATTERN)) {
    const [raw, imageAlt, imageSrc, linkText, linkHref, bold, italic, code] = match;
    const index = match.index ?? 0;

    pushText(input.slice(lastIndex, index));
    lastIndex = index + raw.length;

    if (imageSrc !== undefined) {
      if (isSafeImageUrl(imageSrc)) {
        tokens.push({ type: "image", alt: imageAlt ?? "", src: imageSrc });
      } else {
        // Better to show the alt text than to silently drop the message content.
        pushText(imageAlt || raw);
      }
      continue;
    }

    if (linkHref !== undefined) {
      if (isSafeLinkUrl(linkHref)) {
        tokens.push({ type: "link", value: linkText, href: linkHref });
      } else {
        pushText(linkText);
      }
      continue;
    }

    if (bold !== undefined) {
      tokens.push({ type: "bold", value: bold });
      continue;
    }

    if (italic !== undefined) {
      tokens.push({ type: "italic", value: italic });
      continue;
    }

    if (code !== undefined) {
      tokens.push({ type: "code", value: code });
    }
  }

  pushText(input.slice(lastIndex));

  return tokens;
};
